import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { usePureEffect } from "@buzzeasy/shared-frontend-utilities";
import { Button, Divider, Typography } from "antd";
import { ReactElement, ReactNode, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import ThemeOverrideProvider from "../providers/ThemeOverrideProvider";

export const sidebarMaxWidth = 250;

export interface MainContainerLayoutProps {
  sidebarExpanded: boolean;
  onSidebarExpandedChange(expanded: boolean): void;
  onSidebarTransitioningEnd(): void;
  header?: ReactNode;
  subheader?: ReactNode;
  sidebar?: ReactNode;
  content: ReactNode;
}

export default function MainContainerLayout({ sidebarExpanded, onSidebarExpandedChange, header, subheader, sidebar, content, onSidebarTransitioningEnd }: MainContainerLayoutProps): ReactElement {
  const { t } = useTranslation();

  const sidebarRef = useRef<HTMLDivElement>(null);

  usePureEffect(
    (refs) => {
      const element = sidebarRef.current;
      if (!element) return;

      const handleTransitionEnd = (e: TransitionEvent) => e.target === element && refs.onSidebarTransitioningEnd();
      element.addEventListener("transitionend", handleTransitionEnd);
      return () => element.removeEventListener("transitionend", handleTransitionEnd);
    },
    [],
    { onSidebarTransitioningEnd },
  );

  const sidebarTitle = t("sidebar.sidebarTitle");
  const expandCollapseButtonLabel = t(sidebarExpanded ? "verbs.collapseX" : "verbs.expandX", { value: sidebarTitle }).capitalize();

  return (
    <RootBox id="ctui-root">
      {
        header &&
        <ThemeOverrideProvider overrideTokens={token => ({ colorText: token.colorWhite, colorBgContainer: token.colorPrimary, colorBgBase: token.colorPrimary })}>
          <Header id="ctui-header">
            {header}
          </Header>
        </ThemeOverrideProvider>
      }
      {
        subheader &&
        <Subheader id="ctui-subheader">
          {subheader}
        </Subheader>
      }
      <SideBarAndContent>
        {
          sidebar &&
          <Sidebar $expanded={sidebarExpanded} ref={sidebarRef} id="ctui-sidebar">
            <SidebarHeader>
              {
                sidebarExpanded &&
                <SideBarTitle level={4} style={{ margin: 0 }}>{sidebarTitle}</SideBarTitle>
              }
              <Button
                type="link"
                icon={sidebarExpanded ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                onClick={() => onSidebarExpandedChange(!sidebarExpanded)}
                aria-label={expandCollapseButtonLabel}
              />
            </SidebarHeader>
            <Divider style={{ margin: 0 }} />
            <SidebarContent>
              {sidebar}
            </SidebarContent>
          </Sidebar>
        }
        <Content id="ctui-content">
          {content}
        </Content>
      </SideBarAndContent>
    </RootBox>
  );
}

const SideBarTitle = styled(Typography.Title)`
  margin: 0;
  white-space: nowrap;
`;

const RootBox = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  flex: 0 0 auto;
  min-height: 0;

  padding: ${p => p.theme.paddingXXS}px ${p => p.theme.paddingXS}px;
  background-color: ${p => p.theme.colorBgContainer};
  color: ${p => p.theme.colorWhite};
`;

const Subheader = styled.div`
  flex: 0 0 auto;
  min-height: 0;
`;

const SideBarAndContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
`;

const Sidebar = styled.div<{ $expanded: boolean }>`
  flex: 0 0 auto;
  min-width: 0;
  max-width: ${sidebarMaxWidth}px;

  width: ${p => p.$expanded ? "max-content" : (p.theme.controlHeight + 2 * p.theme.paddingXXS) + "px"};
  overflow-x: hidden;
  transition: width ${p => p.theme.motionDurationMid} ${p => p.theme.motionEaseOut};

  display: flex;
  flex-direction: column;

  border-right: ${p => p.theme.lineWidth}px ${p => p.theme.lineType} ${p => p.theme.colorBorderSecondary};
  box-shadow: ${p => p.theme.size / 8}px 0px ${p => p.theme.size / 4}px 0px rgba(0, 0, 0, 0.05);
`;

const SidebarHeader = styled.div`
  flex: 0 0 auto;
  min-height: 0;

  padding: ${p => p.theme.paddingXXS}px;

  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${p => p.theme.paddingXS}px;
`;

const SidebarContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;

  overflow-x: hidden;
  overflow-y: auto;

  padding: ${p => p.theme.paddingXXS}px;
`;

const Content = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  padding: 0 ${p => p.theme.paddingXXS}px;
`;
